import React, { useEffect, useState, useMemo } from 'react';
import { SubscriptionApi } from '../api';
import type { SubscriptionEvent, SubscriptionStats } from '../api/SubscriptionApi';

const EVENT_COLORS: Record<string, { bg: string; color: string }> = {
  CREATED:         { bg: '#e8f5e9', color: '#2e7d32' },
  RENEWED:         { bg: '#e3f2fd', color: '#1565c0' },
  EXTENDED:        { bg: '#e8eaf6', color: '#3949ab' },
  PLAN_CHANGED:    { bg: '#fff8e1', color: '#f57f17' },
  DAYS_SET:        { bg: '#fce4ec', color: '#880e4f' },
  CANCELLED:       { bg: '#ffebee', color: '#c62828' },
  EXPIRED:         { bg: '#fafafa', color: '#616161' },
  INFINITE_ON:     { bg: '#e0f7fa', color: '#006064' },
  INFINITE_OFF:    { bg: '#fbe9e7', color: '#bf360c' },
  AUTO_RENEW_ON:   { bg: '#f1f8e9', color: '#33691e' },
  AUTO_RENEW_OFF:  { bg: '#fff3e0', color: '#e65100' },
};

function EventBadge({ type }: { type: string }) {
  const style = EVENT_COLORS[type] ?? { bg: '#f5f5f5', color: '#333' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      background: style.bg,
      color: style.color,
      whiteSpace: 'nowrap',
    }}>
      {type}
    </span>
  );
}

function StatCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      padding: '16px 24px',
      minWidth: 140,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: color ?? '#333' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function MonthlyChart({ data, label, color }: {
  data: Array<{ year: number; month: number; count: number }>;
  label: string;
  color: string;
}) {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => Number(d.count)), 1);
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 8, color: '#555' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
        {data.slice(-12).map((d, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: '#666', marginBottom: 2 }}>{d.count}</div>
            <div style={{
              width: '100%',
              height: Math.max(4, (Number(d.count) / max) * 60),
              background: color,
              borderRadius: '3px 3px 0 0',
            }} />
            <div style={{ fontSize: 9, color: '#999', marginTop: 2, overflow: 'hidden', maxWidth: '100%' }}>
              {d.month}/{String(d.year).slice(2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAGE_SIZE = 50;

export default function SubscriptionHistoryPage() {
  const [events, setEvents] = useState<SubscriptionEvent[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    Promise.all([
      SubscriptionApi.getHistory(),
      SubscriptionApi.getStatistics(),
    ])
      .then(([evRes, stRes]) => {
        setEvents(evRes.data);
        setStats(stRes.data);
      })
      .catch(() => setError('Failed to load subscription history'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = events;
    if (filterUser) list = list.filter(e => e.username.toLowerCase().includes(filterUser.toLowerCase()));
    if (filterType) list = list.filter(e => e.eventType === filterType);
    if (filterDateFrom) list = list.filter(e => e.eventTime >= filterDateFrom);
    if (filterDateTo) list = list.filter(e => e.eventTime <= filterDateTo + 'T23:59:59');
    return list;
  }, [events, filterUser, filterType, filterDateFrom, filterDateTo]);

  const pageData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const allTypes = useMemo(() => Array.from(new Set(events.map(e => e.eventType))).sort(), [events]);

  function formatDate(iso: string) {
    if (!iso) return '—';
    return iso.replace('T', ' ').slice(0, 16);
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>;
  if (error) return <div style={{ padding: 40, color: '#c62828' }}>{error}</div>;

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 24, fontSize: 24, fontWeight: 700 }}>Subscription History &amp; Statistics</h2>

      {/* ── Stats Cards ── */}
      {stats && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatCard label="Total Created"   value={stats.totalCreated}   color="#2e7d32" />
            <StatCard label="Total Renewals"  value={stats.totalRenewals}  color="#1565c0" />
            <StatCard label="Total Expired"   value={stats.totalExpired}   color="#616161" />
            <StatCard label="Total Cancelled" value={stats.totalCancelled} color="#c62828" />
            <StatCard label="Total Extended"  value={stats.totalExtended}  color="#3949ab" />
            <StatCard label="Distinct Users (created)"  value={stats.distinctUsersCreated}  color="#333" />
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatCard label="Last 30d Created"   value={stats.last30DaysCreated}   color="#2e7d32" />
            <StatCard label="Last 30d Renewals"  value={stats.last30DaysRenewals}  color="#1565c0" />
            <StatCard label="Last 30d Expired"   value={stats.last30DaysExpired}   color="#616161" />
            <StatCard label="Last 30d Cancelled" value={stats.last30DaysCancelled} color="#c62828" />
          </div>

          {/* Monthly charts */}
          {(stats.monthlyNewSubscriptions?.length > 0 || stats.monthlyRenewals?.length > 0) && (
            <div style={{
              display: 'flex', gap: 32, background: '#fff', border: '1px solid #e0e0e0',
              borderRadius: 10, padding: '20px 24px', flexWrap: 'wrap',
            }}>
              {stats.monthlyNewSubscriptions?.length > 0 && (
                <div style={{ flex: 1, minWidth: 220 }}>
                  <MonthlyChart data={stats.monthlyNewSubscriptions} label="New Subscriptions / Month" color="#43a047" />
                </div>
              )}
              {stats.monthlyRenewals?.length > 0 && (
                <div style={{ flex: 1, minWidth: 220 }}>
                  <MonthlyChart data={stats.monthlyRenewals} label="Renewals / Month" color="#1e88e5" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end',
        background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10,
        padding: '16px 20px', marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Username</div>
          <input
            value={filterUser}
            onChange={e => { setFilterUser(e.target.value); setPage(0); }}
            placeholder="Filter by user…"
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, width: 160 }}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Event type</div>
          <select
            value={filterType}
            onChange={e => { setFilterType(e.target.value); setPage(0); }}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }}
          >
            <option value="">All types</option>
            {allTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>From date</div>
          <input
            type="date"
            value={filterDateFrom}
            onChange={e => { setFilterDateFrom(e.target.value); setPage(0); }}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>To date</div>
          <input
            type="date"
            value={filterDateTo}
            onChange={e => { setFilterDateTo(e.target.value); setPage(0); }}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }}
          />
        </div>
        {(filterUser || filterType || filterDateFrom || filterDateTo) && (
          <button
            onClick={() => { setFilterUser(''); setFilterType(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(0); }}
            style={{
              padding: '6px 14px', background: '#f5f5f5', border: '1px solid #ccc',
              borderRadius: 6, fontSize: 13, cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#888', alignSelf: 'center' }}>
          {filtered.length} event{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── Events Table ── */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
              <th style={th}>Time</th>
              <th style={th}>User</th>
              <th style={th}>Event</th>
              <th style={th}>Plan change</th>
              <th style={th}>Days Δ</th>
              <th style={th}>Performed by</th>
              <th style={th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
                  No events found
                </td>
              </tr>
            ) : pageData.map(ev => (
              <tr key={ev.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={td}>{formatDate(ev.eventTime)}</td>
                <td style={{ ...td, fontWeight: 500 }}>{ev.username}</td>
                <td style={td}><EventBadge type={ev.eventType} /></td>
                <td style={td}>
                  {(ev.oldPlan || ev.newPlan) ? (
                    <span>
                      {ev.oldPlan && <span style={{ color: '#888' }}>{ev.oldPlan}</span>}
                      {ev.oldPlan && ev.newPlan && <span style={{ color: '#bbb', margin: '0 4px' }}>→</span>}
                      {ev.newPlan && <span style={{ color: '#333', fontWeight: 500 }}>{ev.newPlan}</span>}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ ...td, textAlign: 'center', color: ev.daysDelta ? (ev.daysDelta > 0 ? '#2e7d32' : '#c62828') : '#aaa' }}>
                  {ev.daysDelta != null ? (ev.daysDelta > 0 ? `+${ev.daysDelta}` : ev.daysDelta) : '—'}
                </td>
                <td style={{ ...td, color: '#666' }}>{ev.performedBy}</td>
                <td style={{ ...td, color: '#888', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.notes ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
            style={paginBtn}
          >← Prev</button>
          <span style={{ fontSize: 13, color: '#666' }}>
            Page {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            style={paginBtn}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  color: '#555',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '9px 14px',
  verticalAlign: 'middle',
};

const paginBtn: React.CSSProperties = {
  padding: '6px 16px',
  border: '1px solid #ccc',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
  background: '#fff',
};
